import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import TemplatesPage from './pages/TemplatesPage';
import EditorPage from './pages/EditorPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ImagesPage from './pages/ImagesPage';
import LoginPage from './pages/LoginPage';
import knktLogo from './assets/knkt-logo.svg';
import './styles/App.css';

// Handles SSO redirect from knkt.io — stores token from URL, then navigates
function SSOCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshFromStorage } = useAuth();

  useEffect(() => {
    const token    = params.get('token');
    const user     = params.get('user');
    const redirect = params.get('redirect') || '/';
    if (token) {
      localStorage.setItem('knkt_ue_token', token);
      if (user) {
        try { localStorage.setItem('knkt_ue_user', decodeURIComponent(user)); } catch {}
      }
      if (refreshFromStorage) refreshFromStorage();
    }
    navigate(redirect, { replace: true });
  }, []); // eslint-disable-line

  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppContent() {
  const { user, logout } = useAuth();
  // Hide Sign Out when embedded inside KNKT (iframe) — parent app handles auth
  const isEmbedded = window.self !== window.top;

  return (
    <Router>
      <div className="app">
        {user && (
          <header className="app-header">
            <div className="container">
              <Link to="/" className="logo">
                <img src={knktLogo} alt="KNKT" className="logo-image" />
                <span className="logo-text">Email Manager</span>
              </Link>
              <nav className="nav">
                <NavLink to="/" end className="nav-link">Templates</NavLink>
                <NavLink to="/images" className="nav-link">Images</NavLink>
                <NavLink to="/analytics" className="nav-link">Analytics</NavLink>
              </nav>
              <div className="nav-user">
                <span className="nav-user-name">{user.firstName || user.name}</span>
                {!isEmbedded && (
                  <button className="nav-logout-btn" onClick={logout}>Sign Out</button>
                )}
              </div>
            </div>
          </header>
        )}

        <main className="app-main">
          <Routes>
            <Route path="/sso" element={<SSOCallback />} />
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
            <Route path="/" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
            <Route path="/editor" element={<ProtectedRoute><EditorPage /></ProtectedRoute>} />
            <Route path="/editor/:id" element={<ProtectedRoute><EditorPage /></ProtectedRoute>} />
            <Route path="/images" element={<ProtectedRoute><ImagesPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
